import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Martini, ChalkboardTeacher, Sparkle, Star } from "@phosphor-icons/react";
import { Reveal, Overline } from "../components/Reveal";
import { CTASection } from "../components/Sections";
import { useTestimonials } from "../hooks/usePublicContent";
import { IMAGES, CONSULENZE, EVENTI, VALUES, TESTIMONIALS, BRAND } from "../data/content";

const stats = [
  { value: "20+", label: "Anni di esperienza" },
  { value: "500+", label: "Serate & eventi" },
  { value: "AIBM", label: "Socio & docente" },
];

const pillars = [
  { icon: ChalkboardTeacher, title: "Consulenza", text: "Analizzo e sistemo il tuo servizio, la carta e i margini.", to: "/consulenze" },
  { icon: Martini, title: "Formazione", text: "Formo staff, aziende e professionisti con metodo concreto.", to: "/formazione" },
  { icon: Sparkle, title: "Eventi Open Bar", text: "Open Bar tematici che diventano protagonisti della serata.", to: "/eventi" },
];

export default function Home() {
  const testimonials = useTestimonials();
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.hero} alt="Bartender al lavoro" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#09090b] via-[#09090b]/85 to-[#09090b]/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-transparent to-[#09090b]/40" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 pt-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="max-w-3xl">
            <Overline>Consulente · Docente · Open Bar Specialist</Overline>
            <h1 className="mt-5 font-display text-4xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight">
              Trasformo il tuo bar in un'<span className="text-gradient-amber">esperienza</span> che le persone ricordano.
            </h1>
            <p className="mt-7 text-lg sm:text-xl text-[#A1A1AA] max-w-2xl leading-relaxed">
              20 anni dietro il bancone, in sala e nella formazione. Ti aiuto a migliorare servizio, drink list e margini — e progetto Open Bar tematici per i tuoi eventi.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/contatti" data-testid="hero-cta-button" className="group bg-[#D97736] text-white px-8 py-4 rounded-sm font-semibold tracking-wide hover:bg-[#B85B22] transition-all shadow-[0_0_25px_rgba(217,119,54,0.3)] flex items-center justify-center gap-2">
                Richiedi un preventivo
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/eventi" data-testid="hero-secondary-button" className="border border-white/20 text-white px-8 py-4 rounded-sm font-semibold tracking-wide hover:border-[#D97736] hover:text-[#D97736] transition-colors text-center">
                Scopri gli Open Bar
              </Link>
            </div>

            <div className="mt-16 flex flex-wrap gap-10 border-t border-white/10 pt-8">
              {stats.map((s) => (
                <div key={s.label}>
                  <div className="font-display text-3xl sm:text-4xl font-bold text-[#F5A623]">{s.value}</div>
                  <div className="text-sm text-[#A1A1AA] mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <Overline>Cosa offro</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold">Un unico riferimento per far crescere il tuo progetto</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <Link to={p.to} data-testid={`pillar-${i}`} className="block h-full bg-[#111113] border border-white/5 p-8 rounded-sm hover:border-[#D97736]/40 transition-all duration-300 hover:-translate-y-1 group">
                  <div className="w-14 h-14 rounded-sm bg-[#D97736]/10 flex items-center justify-center text-[#D97736] group-hover:bg-[#D97736] group-hover:text-white transition-colors">
                    <p.icon size={28} />
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-semibold">{p.title}</h3>
                  <p className="mt-3 text-[#A1A1AA] leading-relaxed">{p.text}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[#D97736] text-sm font-semibold">
                    Scopri di più <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="py-24 sm:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <img src={IMAGES.about} alt="Ritratto professionale" className="w-full h-[520px] object-cover rounded-sm" />
              <div className="absolute -bottom-6 -right-4 sm:right-6 bg-[#D97736] text-white p-6 rounded-sm max-w-[220px] shadow-2xl">
                <div className="font-display text-3xl font-bold">20 anni</div>
                <div className="text-sm mt-1 text-white/90">sul campo, tra bancone e formazione</div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <Overline>Chi sono</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold">Un professionista che risolve problemi, non solo un bartender</h2>
            <p className="mt-6 text-[#A1A1AA] text-lg leading-relaxed">
              Ho passato la vita dietro e davanti al bancone. Oggi metto quell'esperienza al servizio di locali, aziende e privati come consulente, docente e Open Bar Specialist.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-6">
              {VALUES.map((v) => (
                <div key={v.title}>
                  <h3 className="font-semibold text-[#F8F8F8]">{v.title}</h3>
                  <p className="text-sm text-[#A1A1AA] mt-1">{v.text}</p>
                </div>
              ))}
            </div>
            <Link to="/chi-sono" data-testid="about-more-button" className="mt-9 inline-flex items-center gap-2 text-[#D97736] font-semibold">
              La mia storia <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SERVICES LIST */}
      <section className="py-24 sm:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <Overline>Consulenze</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold">Pacchetti pensati per ogni esigenza</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {CONSULENZE.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.08}>
                <div className="bg-[#111113] border border-white/5 p-8 rounded-sm hover:border-[#D97736]/40 transition-all h-full">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-display text-2xl font-semibold">{c.title}</h3>
                    <span className="text-xs text-[#D97736] border border-[#D97736]/30 px-2 py-1 rounded-sm whitespace-nowrap">0{i + 1}</span>
                  </div>
                  <p className="mt-3 text-[#D97736] text-sm">{c.tagline}</p>
                  <p className="mt-4 text-[#A1A1AA] leading-relaxed">{c.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/consulenze" data-testid="services-more-button" className="inline-flex items-center gap-2 text-[#D97736] font-semibold">
              Tutti i dettagli delle consulenze <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* EVENTS PREVIEW */}
      <section className="py-24 sm:py-32 border-t border-white/5 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <Overline>Eventi Open Bar tematici</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold">Tre livelli di esperienza, un solo obiettivo: stupire</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {EVENTI.map((e, i) => (
              <Reveal key={e.tier} delay={i * 0.1}>
                <div className={`p-8 rounded-sm h-full transition-all ${e.featured ? "bg-gradient-to-b from-[#1a1105] to-[#111113] border border-[#D97736]/40" : "bg-[#111113] border border-white/5"}`}>
                  <span className="text-xs tracking-[0.2em] uppercase text-[#D97736] font-semibold">{e.tier}</span>
                  <h3 className="mt-3 font-display text-2xl font-semibold">{e.title}</h3>
                  <p className="mt-4 text-[#A1A1AA] leading-relaxed text-sm">{e.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/eventi" data-testid="events-more-button" className="inline-flex items-center gap-2 text-[#D97736] font-semibold">
              Scopri gli Open Bar <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 sm:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <Overline>Dicono di me</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold">Risultati che parlano</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <Reveal key={t.id || t.name} delay={i * 0.1}>
                <div className="bg-[#111113] border border-white/5 p-8 rounded-sm h-full flex flex-col">
                  <div className="flex gap-1 text-[#F5A623]">
                    {[...Array(5)].map((_, k) => <Star key={k} size={16} weight="fill" />)}
                  </div>
                  <p className="mt-5 text-[#F8F8F8] leading-relaxed flex-1">"{t.text}"</p>
                  <div className="mt-6 pt-5 border-t border-white/5">
                    <div className="font-semibold">{t.name}</div>
                    <div className="text-sm text-[#A1A1AA]">{t.role}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
